'use client';
import React from 'react';
import { BehaviorLog } from '@/lib/services/forgeBehaviorService';
import { forgeBehaviorService } from '@/lib/services/forgeBehaviorService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface BehaviorDashboardProps {
  logs: BehaviorLog[];
  frequency: ReturnType<typeof forgeBehaviorService.getBehaviorFrequency>;
}

const FORGE_COLORS = {
  primary: '#22c55e',
  accent: '#f59e0b',
  muted: '#6b7280',
  cannabis: '#a78bfa',
  alcohol: '#f59e0b',
  stress: '#ef4444',
  sleep: '#60a5fa',
};

interface FreqBarProps {
  label: string;
  count: number;
  pct: number;
  total: number;
  color: string;
}

function FreqBar({ label, count, pct, total, color }: FreqBarProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold text-foreground">{count}/{total} days <span className="text-muted-foreground">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-border rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function BehaviorDashboard({ logs, frequency }: BehaviorDashboardProps) {
  // Build chart data for last 14 days
  const chartData = logs.slice(0, 14).reverse().map(l => ({
    date: l.logDate.slice(5),
    calories: l.totalCalories,
    protein: l.totalProteinG,
    forgeScore: l.forgeScore,
    hasCannabis: l.cannabisAmount !== 'none',
    hasStress: l.wasHighStress,
  }));

  // Average forge score
  const avgForgeScore = logs.length > 0
    ? Math.round(logs.reduce((s, l) => s + l.forgeScore, 0) / logs.length)
    : 0;

  const avgSleep = (() => {
    const withSleep = logs.filter(l => l.sleepDurationHours != null);
    if (!withSleep.length) return null;
    return (withSleep.reduce((s, l) => s + (l.sleepDurationHours || 0), 0) / withSleep.length).toFixed(1);
  })();

  return (
    <div className="space-y-5">
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="card-forge p-4 text-center">
          <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Days Tracked</div>
          <div className="text-2xl font-black text-foreground">{frequency.total}</div>
        </div>
        <div className="card-forge p-4 text-center">
          <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Avg Forge Score</div>
          <div className="text-2xl font-black text-primary">{avgForgeScore}</div>
        </div>
        <div className="card-forge p-4 text-center">
          <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Avg Sleep</div>
          <div className="text-2xl font-black text-blue-400">{avgSleep ?? '—'}{avgSleep ? 'h' : ''}</div>
        </div>
        <div className="card-forge p-4 text-center">
          <div className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Cannabis Days</div>
          <div className="text-2xl font-black text-violet-400">{frequency.cannabis}</div>
        </div>
      </div>

      {/* Behavior frequency */}
      <div className="card-forge p-5 space-y-4">
        <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">Behavior Frequency</h3>
        <div className="space-y-3">
          <FreqBar label="Cannabis Use" count={frequency.cannabis} pct={frequency.cannabisPct} total={frequency.total} color={FORGE_COLORS.cannabis} />
          <FreqBar label="Alcohol" count={frequency.alcohol} pct={frequency.alcoholPct} total={frequency.total} color={FORGE_COLORS.alcohol} />
          <FreqBar label="Late Night Eating" count={frequency.lateNightEating} pct={frequency.lateNightEatingPct} total={frequency.total} color={FORGE_COLORS.accent} />
          <FreqBar label="Takeaway Meals" count={frequency.takeaway} pct={frequency.takeawayPct} total={frequency.total} color={FORGE_COLORS.accent} />
          <FreqBar label="High Stress Days" count={frequency.stress} pct={frequency.stressPct} total={frequency.total} color={FORGE_COLORS.stress} />
          <FreqBar label="Social Events" count={frequency.socialEvents} pct={frequency.socialEventsPct} total={frequency.total} color={FORGE_COLORS.sleep} />
          <FreqBar label="Energy Drinks" count={frequency.energyDrinks} pct={frequency.energyDrinksPct} total={frequency.total} color={FORGE_COLORS.muted} />
        </div>
      </div>

      {/* Forge Score trend */}
      {chartData.length >= 3 && (
        <div className="card-forge p-4 sm:p-5 space-y-3">
          <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">Forge Score Trend</h3>
          <div className="h-36 sm:h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#6b7280' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} domain={[0, 100]} width={28} />
                <Tooltip
                  contentStyle={{ background: '#1a1a2e', border: '1px solid #374151', borderRadius: 6, fontSize: 11 }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Bar dataKey="forgeScore" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.hasCannabis ? FORGE_COLORS.cannabis : entry.hasStress ? FORGE_COLORS.stress : FORGE_COLORS.primary}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-2xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: FORGE_COLORS.primary }} />
              Normal day
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: FORGE_COLORS.cannabis }} />
              Cannabis day
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: FORGE_COLORS.stress }} />
              Stress day
            </div>
          </div>
        </div>
      )}

      {/* Calorie trend */}
      {chartData.length >= 3 && (
        <div className="card-forge p-4 sm:p-5 space-y-3">
          <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">Calorie Trend</h3>
          <div className="h-36 sm:h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#6b7280' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} width={32} />
                <Tooltip
                  contentStyle={{ background: '#1a1a2e', border: '1px solid #374151', borderRadius: 6, fontSize: 11 }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Bar dataKey="calories" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.hasCannabis ? FORGE_COLORS.cannabis : FORGE_COLORS.accent}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
